/* eslint-disable */
import personalInfoZ, {
  PersonalInfoType,
} from '@/components/dashboard/members/profile/types'
import { UpdateProfileDocument } from '@/types/graphql/types'
import { getErrorMessage } from '@/util/getErrorMessage'
import handleImage from '@/util/imageUpload'
import { parser } from '@/util/parser'
import { useMutation } from '@apollo/client'
import { useForm } from '@mantine/form'
import { showNotification } from '@mantine/notifications'
import { useModals } from '@mantine/modals'
import { ROLE } from 'db'
import { useSession } from 'next-auth/react'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import { useState, useRef } from 'react'
import 'react-phone-number-input/style.css'
import { AvatarModal } from '@/components/client/profile/AvatarModal'
import containsSpecialChars from '@/util/specialchar'
import { isValidPhoneNumber } from 'libphonenumber-js'

type UserInfo = PersonalInfoType & {
  scfhsOption?: string
  rank?: number
}

const useProfileBasicInfoForm = ({
  profile: profileUser,
  refetch,
}: {
  profile: PersonalInfoType
  refetch?: () => void
}) => {
  let profile: UserInfo = profileUser
  profile.scfhsOption =
    profileUser.scfhs && profileUser.scfhs?.length > 0 ? 'yes' : ''
  const { t, lang } = useTranslation('client')
  const yesNoRef = useRef<any>()
  const [uploadImageLoading, setUploadImageLoading] = useState(false)
  const [uploadCVLoading, setUploadCVLoading] = useState(false)
  const phoneNumberRef = useRef<any>()
  const modals = useModals()
  const isArabic = lang === 'ar'
  const { query, push, back, asPath } = useRouter()
  const { data } = useSession()
  const isTrainer = data?.user.roleId === ROLE.trainer.id
  const isBecomeTrainer = asPath.includes('become_trainer')
  const executeScroll = (ref: any) => ref.current?.scrollIntoView()

  const form = useForm({
    initialValues: profile,
    validate: {
      mobileNumber: value =>
        !value.startsWith('+', 0)
          ? t('mobileNumberCodeError')
          : !isValidPhoneNumber(value)
          ? t('mobileNumberNotValidError')
          : null,
      scfhsOption: value =>
        !['yes', 'no'].includes(value!) ? t('fillData') : null,
      name: value =>
        /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(value)
          ? t('invalidEnglishName')
          : null,
      nameAr: value => (/[a-zA-Z]/.test(value) ? t('invalidArabicName') : null),
      scfhs: value => (containsSpecialChars(value) ? t('scfhsCharErr') : null),
    },
  })

  //update profile mutation
  const [updateProfile, { loading }] = useMutation(UpdateProfileDocument, {
    onCompleted: () => {
      showNotification({
        title: t('profile'),
        message: t('profileUpdateSuccess'),
      })
      if (refetch) refetch()
      if (query.callback) {
        push(((query.callback || '') as string).replaceAll('*', '&'))
      }
    },
    onError(error) {
      showNotification({
        title: t('error'),
        message: getErrorMessage(error),
        color: 'red',
        autoClose: false,
      })
    },
  })
  //auto scroll to error element
  if (form.errors.scfhsOption) {
    executeScroll(yesNoRef)
  } else if (form.errors.mobileNumber) {
    showNotification({
      title: t('error'),
      message: form.errors.mobileNumber,
      color: 'red',
      autoClose: false,
    })
    executeScroll(phoneNumberRef)
  }
  //submit profile update data
  const handleUpdate = (values: typeof form.values) => {
    const zod = personalInfoZ({ operation: 'update' }).safeParse(
      parser.is(values).deepOmitNull().value,
    )
    if (!zod.success) {
      console.log(zod)
    }
    if (zod.success) {
      updateProfile({
        variables: {
          data: {
            ...zod.data,
            ...(values.scfhsOption === 'no' && { scfhs: { set: null } }),
            trainerCv: {
              upsert: {
                create: {
                  rankId: +values.trainerCv?.rank,
                  qualificationId: +values.trainerCv?.qualification,
                },
                update: {
                  rankId: { set: +values.trainerCv?.rank },
                  qualificationId: {
                    set: +values.trainerCv?.qualification,
                  },
                },
              },
            },
          },
          where: { id: profile.id },
        },
      })
    }
  }
  //upload avatar
  const uploadAvatar = (image: any) => {
    if (image) {
      modals.closeAll()
      setUploadImageLoading(true)
      handleImage(image).then((link: string) => {
        form.setFieldValue('image', link)
        setUploadImageLoading(false)
      })
    }
  }
  //open avatar modal
  const openAvatar = (image: any) => {
    modals.openModal({
      title: t('updateAvatar'),
      children: (
        <AvatarModal
          removeAvatar={() => form.setFieldValue('image', '')}
          image={image}
          uploadAvatar={e => uploadAvatar(e)}
        />
      ),
    })
  }

  return {
    openAvatar,
    handleUpdate,
    form,
    uploadImageLoading,
    back,
    loading,
    phoneNumberRef,
    uploadCVLoading,
    isTrainer,
    yesNoRef,
    isArabic,
    setUploadCVLoading,
    isBecomeTrainer,
  }
}

export default useProfileBasicInfoForm
